import {
  createAppSelector,
  selectCurrentSectionId,
  selectPageGroupUsers,
  selectPageGroups,
  selectPages,
} from "./base";
import { selectEnabledSectionUsers } from "./section-user";
import type { ExtendedPageGroupUserType, NestedPageGroupType } from "./types";

export const selectPageGroupsByPageId = createAppSelector(
  [(state, pageId: string) => pageId, selectPageGroups],
  (pageId, pageGroups) => pageGroups.filter((page_group) => page_group.page_id === pageId),
);

export const selectPageGroupUsersByPageId = createAppSelector(
  [
    (state, pageId: string) => pageId,
    selectPageGroupUsers,
    (state) => state.page_groups,
    selectEnabledSectionUsers,
    (state) => state.users,
  ],
  (
    pageId,
    pageGroupUsers,
    pageGroupMap,
    enabledSectionUsers,
    userMap,
  ): ExtendedPageGroupUserType[] => {
    return pageGroupUsers.flatMap((pgu) => {
      const group = pageGroupMap[pgu.page_group_id];
      if (!group || group.page_id !== pageId) {
        return [];
      }

      const enabledSectionUser = enabledSectionUsers.find((cu) => cu.id === pgu.section_user_id);
      if (!enabledSectionUser) {
        return [];
      }

      const user = userMap[enabledSectionUser.user_id];
      if (!user) {
        return [];
      }

      return [
        {
          ...pgu,
          user_id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
      ];
    });
  },
);

export const selectPageGroupsWithUsersByPageId = createAppSelector(
  [selectPageGroupsByPageId, selectPageGroupUsersByPageId],
  (pageGroups, pageGroupUsers) =>
    pageGroups.map((page_group): NestedPageGroupType => {
      const pageGroupUsersForPageGroup = pageGroupUsers.filter(
        (pgu) => pgu.page_group_id === page_group.id,
      );
      return {
        ...page_group,
        page_group_users: pageGroupUsersForPageGroup,
      };
    }),
);

export const selectPageGroupUserForUser = createAppSelector(
  [
    (state, pageId: string) => pageId,
    (state, pageId: string, userId: string) => userId,
    selectPageGroupsByPageId,
    selectPageGroupUsers,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, pageGroupsForPage, pageGroupUsers, sectionUsers) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIds = sectionUsers
      .filter((sectionUser) => sectionUser.user_id === userId)
      .map((sectionUser) => sectionUser.id);

    const pageGroupIdsForPage = new Set(pageGroupsForPage.map((pg) => pg.id));

    const userPageGroupUser = pageGroupUsers.find(
      (pgu) =>
        sectionUserIds.includes(pgu.section_user_id) && pageGroupIdsForPage.has(pgu.page_group_id),
    );

    return userPageGroupUser ?? null;
  },
);

export const selectNestedPageGroupForUser = createAppSelector(
  [
    (state, pageId: string) => pageId,
    (state, pageId: string, userId: string) => userId,
    selectPageGroupsWithUsersByPageId,
    selectPageGroupUserForUser,
  ],
  (pageId, userId, nestedPageGroups, pageGroupUser): NestedPageGroupType | null => {
    if (!pageGroupUser) return null;
    return nestedPageGroups.find((pg) => pg.id === pageGroupUser.page_group_id) ?? null;
  },
);

export const selectPageGroupForReadyUser = createAppSelector(
  [
    (state, pageId: string) => pageId,
    (state, pageId: string, userId: string) => userId,
    selectPageGroups,
    selectPageGroupUsers,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, page_groups, page_group_users, section_users) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIds = section_users
      .filter((section_user) => section_user.user_id === userId)
      .map((section_user) => section_user.id);

    // Get a list of the user's page group ids
    const userGroupIds = page_group_users
      .filter(
        (group_user) => sectionUserIds.includes(group_user.section_user_id) && group_user.is_ready,
      )
      .map((group_user) => group_user.page_group_id);

    // Go through the groups and find the group for the given page and user
    return page_groups.find(
      (page_group) => page_group.page_id === pageId && userGroupIds.includes(page_group.id),
    );
  },
);

export const selectReadiedUsersInPageGroup = createAppSelector(
  [
    (state, pageGroupId: string) => pageGroupId,
    selectPageGroupUsers,
    selectEnabledSectionUsers,
    (state) => state.users,
  ],
  (pageGroupId, page_group_users, section_users, users) => {
    if (!pageGroupId) {
      return [];
    }

    // Get a list of the section_user's for this group
    const sectionUserIds = page_group_users
      .filter((group_user) => group_user.page_group_id === pageGroupId && group_user.is_ready)
      .map((group_user) => group_user.section_user_id);

    // Get a list of all users that match a section_user.user_id
    return section_users
      .filter((section_user) => sectionUserIds.includes(section_user.id))
      .map((section_user) => {
        const user = users[section_user.user_id];
        return {
          ...user,
        };
      });
  },
);

export const selectPageGroupsForCurrentSection = createAppSelector(
  [selectPageGroups, selectPages, selectCurrentSectionId],
  (pageGroups, pages, currentSectionId) => {
    const pagesForCurrentSection = pages.filter((page) => page.section_id === currentSectionId);
    const pageIds = new Set(pagesForCurrentSection.map((page) => page.id));
    return pageGroups.filter((pageGroup) => pageIds.has(pageGroup.page_id));
  },
);

export const selectReleasedPageGroupsForCurrentSection = createAppSelector(
  [selectPageGroups, selectPages, selectCurrentSectionId],
  (pageGroups, pages, currentSectionId) => {
    const releasePagesForCurrentSection = pages.filter(
      (page) => page.section_id === currentSectionId && page.released_at,
    );
    const pageIds = new Set(releasePagesForCurrentSection.map((page) => page.id));
    return pageGroups.filter((pageGroup) => pageIds.has(pageGroup.page_id));
  },
);

export const selectPageGroupUsersForCurrentSection = createAppSelector(
  [selectPageGroupUsers, selectReleasedPageGroupsForCurrentSection],
  (pageGroupUsers, pageGroups) => {
    const pageGroupIds = new Set(pageGroups.map((pageGroup) => pageGroup.id));
    return pageGroupUsers.filter((pageGroupUser) => pageGroupIds.has(pageGroupUser.page_group_id));
  },
);
